import {
  Button,
  FieldError,
  Select,
  SelectOption,
  SelectSizeVariant,
} from '@payaca/ambrosia';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useEffect, useMemo, useState } from 'react';
import { usePageValidation } from '../../context/PageValidationContext';

import { PIPELINE_GUARD_SIMPLE_CONDITION_TYPES } from '@/helpers/pipelinesHelper';
import { useUserHasPermission } from '@/hooks/usePermissions';
import { usePipelineGuardDescriptions } from '@/hooks/usePipelineGuardDescriptions';
import { useCustomFieldsForRole } from '@/hooks/useCustomFieldsForRole';
import { useTranslation } from '@/i18n';

import { PipelineGuardSimpleConditionType } from '@/gql/graphql';
import {
  GuardValidity,
  PipelineGuardAndChild,
  PipelineGuardNot,
  PipelineGuardSimple,
} from '../../types/pipelineGuardTypes';
import { COMPLEX_CONDITIONS } from './complexConditions/complexConditionDefs';
import { CustomFieldConditionExtra } from './complexConditions/CustomFieldConditionExtraPicker';
import { useCustomFieldConditionExtraValidator } from './complexConditions/useCustomFieldConditionExtraValidator';

const CUSTOM_FIELD_CONDITION_KEY = 'CUSTOM_FIELD_CONDITION';

const INVERSE_CONDITION_PREFIX = '_INVERSE_';

function getLeafValidity(
  value: PipelineGuardAndChild
): GuardValidity | undefined {
  const inner = value.type === 'not' ? value.child : value;
  return inner.validityResult;
}

type DerivedDefaults = {
  conditionType: string;
  extra: unknown;
};

function deriveDefaultValues(value: PipelineGuardAndChild): DerivedDefaults {
  const inner = value.type === 'not' ? value.child : value;
  const isInverse = value.type === 'not';

  for (const def of COMPLEX_CONDITIONS) {
    if (def.matches(inner)) {
      const baseKey = isInverse
        ? `${INVERSE_CONDITION_PREFIX}${def.key}`
        : def.key;
      return { conditionType: baseKey, extra: def.deriveExtra(inner) };
    }
  }

  if (inner.type === 'simple') {
    const baseKey = isInverse
      ? `${INVERSE_CONDITION_PREFIX}${inner.conditionType}`
      : inner.conditionType;
    return { conditionType: baseKey, extra: {} };
  }

  // Unreachable: COMPLEX_CONDITIONS handles all non-simple types, but
  // TypeScript can't narrow through the for loop to prove this.
  throw new Error(`Unhandled guard condition type: ${inner.type}`);
}

function toGuardAndChild(
  conditionType: string,
  extra: unknown,
  tempIdentifier: string
): PipelineGuardAndChild {
  const isInverse = conditionType.startsWith(INVERSE_CONDITION_PREFIX);
  const baseType = isInverse
    ? conditionType.replace(INVERSE_CONDITION_PREFIX, '')
    : conditionType;

  // Check complex conditions
  const complexDef = COMPLEX_CONDITIONS.find((c) => c.key === baseType);
  if (complexDef) {
    const child = complexDef.toChild(extra, tempIdentifier);
    if (isInverse) {
      // The condition selector filters defs without `inverseLabel` out
      // of the inverse list, so reaching here with a non-invertible def
      // (e.g. `customFieldCondition`, whose operators self-negate) is
      // unreachable in practice. The dispatch through
      // `AnyComplexConditionDef` erases child's narrow type so building
      // a `not` wrapper here type-checks without a cast.
      return {
        type: 'not',
        child,
        tempIdentifier,
      } satisfies PipelineGuardNot;
    }
    return child;
  }

  // Simple condition
  const simpleChild: PipelineGuardSimple = {
    type: 'simple',
    conditionType: baseType as PipelineGuardSimpleConditionType,
    tempIdentifier,
  };

  if (isInverse) {
    return {
      type: 'not',
      child: simpleChild,
      tempIdentifier,
    } satisfies PipelineGuardNot;
  }
  return simpleChild;
}

type Props = {
  value: PipelineGuardAndChild;
  onSubmit: (guard: PipelineGuardAndChild) => void;
  onRemove: () => void;
};

const ConditionSelectedDisplay: FC<{
  selectedOptions: SelectOption<string>[];
}> = (props) => {
  const { selectedOptions } = props;
  const t = useTranslation();
  return (
    <>
      {selectedOptions
        .map((option) =>
          option.value === CUSTOM_FIELD_CONDITION_KEY
            ? t(
                'pipelineGuards.conditions.CUSTOM_FIELD_CONDITION.selectedLabel'
              )
            : option.label
        )
        .join(', ')}
    </>
  );
};

const GuardConditionRow: FC<Props> = (props) => {
  const { value, onSubmit, onRemove } = props;

  /**
   * global state
   */
  const t = useTranslation();
  const { getHasBeen, getHasNotBeen } = usePipelineGuardDescriptions();
  const { setError, clearError } = usePageValidation();
  const validateCustomFieldConditionExtra =
    useCustomFieldConditionExtraValidator();

  // Gate the "Custom field…" entry: hide it for callers without the
  // `GET_CUSTOM_FIELD_GROUPS` permission, and for accounts that have no
  // project Custom Fields configured at all (the picker would be empty).
  // The currently-active value is exempt from filtering — if a guard was
  // already authored against a CFC and the account loses fields later,
  // the row still needs to render its existing condition.
  const canReadCustomFieldGroups = useUserHasPermission({
    permissions: [AccountsPermissions.GET_CUSTOM_FIELD_GROUPS],
  });
  const { customFields: projectCustomFields } =
    useCustomFieldsForRole('project');
  const hasProjectCustomFields = projectCustomFields.some(
    (field) => !field.archivedAt && field.type === 'fieldset'
  );

  /**
   * derived state
   */
  const defaults = useMemo(() => deriveDefaultValues(value), [value]);

  /**
   * state
   */
  const [conditionType, setConditionType] = useState(defaults.conditionType);
  const [extraValues, setExtraValues] = useState<unknown>(defaults.extra);
  const [extraErrors, setExtraErrors] = useState<Record<string, string>>({});

  // Server-side validity is owned by the parent (it lives on the guard
  // returned from the API). Deriving it instead of mirroring it into local
  // state keeps the input from being torn down on every onSubmit echo,
  // which is what previously dropped focus after a single keystroke.
  const serverValidity = getLeafValidity(value);

  /**
   * effects
   */

  // Register page-level error when server reports invalid guard
  const serverValidityErrorId = `${value.tempIdentifier}-validity`;
  useEffect(() => {
    if (serverValidity && !serverValidity.isValid) {
      setError(serverValidityErrorId);
    } else {
      clearError(serverValidityErrorId);
    }
  }, [serverValidity, serverValidityErrorId, setError, clearError]);

  // Clear validation errors on unmount (e.g. when row is removed)
  useEffect(() => {
    return () => {
      clearError(value.tempIdentifier);
      clearError(serverValidityErrorId);
    };
  }, [clearError, value.tempIdentifier, serverValidityErrorId]);

  /**
   * callbacks
   */
  const handleChange = (nextConditionType: string, nextExtra: unknown) => {
    const baseType = nextConditionType.replace(INVERSE_CONDITION_PREFIX, '');
    const complexDef = COMPLEX_CONDITIONS.find((c) => c.key === baseType);

    if (complexDef) {
      const result = complexDef.extraSchema.safeParse(nextExtra);
      if (!result.success) {
        const fieldErrors: Record<string, string> = {};
        for (const issue of result.error.issues) {
          const key = issue.path[0];
          if (typeof key === 'string') {
            fieldErrors[key] = issue.message;
          }
        }
        setExtraErrors(fieldErrors);
        setError(value.tempIdentifier);
        return;
      }
    }

    if (baseType === CUSTOM_FIELD_CONDITION_KEY) {
      const cfcErrors = validateCustomFieldConditionExtra(
        nextExtra as CustomFieldConditionExtra
      );
      if (cfcErrors) {
        setExtraErrors(cfcErrors);
        setError(value.tempIdentifier);
        return;
      }
    }

    setExtraErrors({});
    clearError(value.tempIdentifier);
    onSubmit(
      toGuardAndChild(nextConditionType, nextExtra, value.tempIdentifier)
    );
  };

  /**
   * Active complex definition
   */
  const activeComplexDef = useMemo(() => {
    const baseType = conditionType.replace(INVERSE_CONDITION_PREFIX, '');
    return COMPLEX_CONDITIONS.find((c) => c.key === baseType);
  }, [conditionType]);

  /**
   * Condition options
   */
  const isComplexConditionAvailable = (key: string): boolean => {
    if (key !== CUSTOM_FIELD_CONDITION_KEY) return true;
    // Always preserve the option if it's the currently-selected one,
    // even if availability has changed since authoring — otherwise the
    // dropdown would silently drop the row's chosen condition.
    if (
      conditionType === CUSTOM_FIELD_CONDITION_KEY ||
      conditionType ===
        `${INVERSE_CONDITION_PREFIX}${CUSTOM_FIELD_CONDITION_KEY}`
    ) {
      return true;
    }
    return canReadCustomFieldGroups && hasProjectCustomFields;
  };

  const conditionOptions: SelectOption<string>[] = [
    ...PIPELINE_GUARD_SIMPLE_CONDITION_TYPES.map((type) => ({
      value: type,
      label: getHasBeen(type),
      groupId: 'CONDITIONS',
    })),
    ...COMPLEX_CONDITIONS.filter((def) =>
      isComplexConditionAvailable(def.key)
    ).map((def) => ({
      value: def.key,
      label: def.label(t),
      groupId: 'CONDITIONS',
    })),
    ...PIPELINE_GUARD_SIMPLE_CONDITION_TYPES.map((type) => ({
      value: `${INVERSE_CONDITION_PREFIX}${type}`,
      label: getHasNotBeen(type),
      groupId: INVERSE_CONDITION_PREFIX,
    })),
    ...COMPLEX_CONDITIONS.filter(
      (def) => def.inverseLabel && isComplexConditionAvailable(def.key)
    ).map((def) => ({
      value: `${INVERSE_CONDITION_PREFIX}${def.key}`,
      // Filter above guarantees inverseLabel is defined.
      label: def.inverseLabel!(t),
      groupId: INVERSE_CONDITION_PREFIX,
    })),
  ];

  return (
    <div data-testid="guard-condition-row">
      <div className="flex items-center gap-2 w-full">
        <div className="shrink grow min-w-0">
          <Select
            allowWrap={true}
            optionGroups={[
              { id: 'CONDITIONS', label: '' },
              {
                id: INVERSE_CONDITION_PREFIX,
                label: 'Inverse conditions',
              },
            ]}
            sizeVariant={SelectSizeVariant.SM}
            options={conditionOptions}
            CustomSelected={ConditionSelectedDisplay}
            multiple={false}
            value={conditionType}
            onChange={(nextConditionType) => {
              setConditionType(nextConditionType);
              // When changing condition type, reset extra values unless staying
              // within the same complex condition
              const nextBase = nextConditionType.replace(
                INVERSE_CONDITION_PREFIX,
                ''
              );
              const currentBase = conditionType.replace(
                INVERSE_CONDITION_PREFIX,
                ''
              );
              const nextExtra = nextBase === currentBase ? extraValues : {};
              setExtraValues(nextExtra);
              handleChange(nextConditionType, nextExtra);
            }}
          />
        </div>
        <Button variant="quaternary" size={'xs'} onClick={onRemove}>
          <span className="sr-only">Remove</span>
          <UntitledIcon className="h-3 w-3" name="x-close" />
        </Button>
      </div>
      {activeComplexDef && (
        <activeComplexDef.ExtraPicker
          value={extraValues}
          onChange={(nextExtra) => {
            setExtraValues(nextExtra);
            handleChange(conditionType, nextExtra);
          }}
          errors={extraErrors}
        />
      )}
      {serverValidity && !serverValidity.isValid && (
        <FieldError messages={serverValidity.reasons} />
      )}
    </div>
  );
};

export default GuardConditionRow;
